import { t } from "@lingui/macro";
import { captureException } from "@sentry/react-native";
import { File as ExpoFile } from "expo-file-system";
import * as ImagePicker from "expo-image-picker";
import { fetch as expoFetch } from "expo/fetch";
import { useState } from "react";
import { Alert, Platform } from "react-native";
import { useMutation } from "react-relay";

import { useFlags } from "@/utils/launchDarkly";
import {
  imageFormatFromFileName,
  imageFormatFromMimeType,
  uploadImageKitFile,
} from "@whoosh/utilities/src/ImageKit";

import { getExpoEASConstant } from "../../../../../utils/expoConstants";
import { ImageObjectStateProps } from "../../../types";
import { usePatronImageKitAuthMutation } from "./__generated__/usePatronImageKitAuthMutation.graphql";
import { patronImageKitAuthMutation } from "./usePatronImageKitAuth";

type CloudinaryUploadResponse = {
  format: string;
  height: number;
  public_id: string;
  width: number;
};

const isCloudinaryUploadResponse = (
  value: unknown
): value is CloudinaryUploadResponse => {
  if (!value || typeof value !== "object") return false;
  return (
    "public_id" in value &&
    typeof value.public_id === "string" &&
    "height" in value &&
    typeof value.height === "number" &&
    "width" in value &&
    typeof value.width === "number" &&
    "format" in value &&
    typeof value.format === "string"
  );
};

const reportImageUploadError = (error: unknown) => {
  captureException(
    error instanceof Error
      ? error
      : new Error("An unknown profile image upload error occurred")
  );
  Alert.alert(t`An Error Occurred While Uploading`);
};

export const getWebImageFile = async (
  asset: ImagePicker.ImagePickerAsset
): Promise<File> => {
  if (asset.file) {
    return asset.file;
  }

  const response = await fetch(asset.uri);
  if (!response.ok) {
    throw new Error("Unable to read the selected image");
  }

  const blob = await response.blob();
  return new File([blob], asset.fileName ?? "profile-image.jpg", {
    type: asset.mimeType ?? blob.type,
  });
};

export const getNativeImageFile = (
  asset: ImagePicker.ImagePickerAsset
): ExpoFile => new ExpoFile(asset.uri);

const usePickImage = (clubId?: string | null) => {
  const { imagekitImageUploads } = useFlags();
  const [requestAuth] = useMutation<usePatronImageKitAuthMutation>(
    patronImageKitAuthMutation
  );

  const [removedImage, setRemovedImage] = useState<boolean>(false);
  const [imageObject, setImageObject] = useState<ImageObjectStateProps | null>(
    null
  );
  const [isImageUploading, setIsImageUploading] = useState(false);

  const pickImage = async () => {
    if (Platform.OS !== "web") {
      const { status } =
        await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        alert(t`Sorry, we need camera roll permissions to make this work!`);
        return;
      }
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
    });

    const asset = result.assets ? result.assets.find((a) => a.uri) : null;

    if (result.canceled || !asset) {
      return;
    }

    if (Platform.OS === "web") {
      setIsImageUploading(true);

      try {
        const file = await getWebImageFile(asset);
        upload(file, asset.mimeType ?? file.type, file.name);
      } catch (error: unknown) {
        reportImageUploadError(error);
        setIsImageUploading(false);
      }
    } else {
      const file = getNativeImageFile(asset);
      upload(
        file,
        asset.mimeType,
        file.extension || asset.fileName || file.name
      );
    }
  };

  const upload = async (
    photo: File | ExpoFile,
    mimeType?: string,
    fileNameOrExtension?: string | null
  ) => {
    setIsImageUploading(true);

    try {
      // TODO(ENG-3436): Remove Cloudinary fallback after ImageKit uploads are enabled for all clients and rollback is retired.
      if (!imagekitImageUploads) {
        const data = new FormData();
        data.append("file", photo);
        data.append(
          "upload_preset",
          getExpoEASConstant("cloudinaryUploadPreset")
        );
        data.append("cloud_name", getExpoEASConstant("cloudinaryCloudName"));
        const response = await expoFetch(
          getExpoEASConstant("cloudinaryImgUploadUrl"),
          { method: "post", body: data }
        );
        if (!response.ok) {
          throw new Error(
            `Cloudinary image upload failed with status ${response.status}`
          );
        }
        const responseBody: unknown = await response.json();
        if (!isCloudinaryUploadResponse(responseBody)) {
          throw new Error(
            "Cloudinary image upload returned an invalid response"
          );
        }
        setImageObject({
          publicId: responseBody.public_id,
          height: responseBody.height,
          width: responseBody.width,
          format: responseBody.format,
        });
        setRemovedImage(false);
        return;
      }
      if (!clubId) {
        throw new Error("No club is selected for image upload");
      }
      const format = mimeType
        ? imageFormatFromMimeType(mimeType)
        : imageFormatFromFileName(fileNameOrExtension ?? "");
      const auth = await new Promise<
        NonNullable<
          usePatronImageKitAuthMutation["response"]["imagekitUploadAuth"]
        >
      >((resolve, reject) => {
        requestAuth({
          variables: { clubId, format },
          onCompleted: (response, errors) => {
            if (errors?.length || !response.imagekitUploadAuth) {
              reject(new Error("ImageKit upload authorization failed"));
            } else {
              resolve(response.imagekitUploadAuth);
            }
          },
          onError: reject,
        });
      });
      const image = await uploadImageKitFile(photo, auth, expoFetch);
      setImageObject(image);
      setRemovedImage(false);
    } catch (error: unknown) {
      reportImageUploadError(error);
    } finally {
      setIsImageUploading(false);
    }
  };

  return {
    pickImage,
    removedImage,
    setRemovedImage,
    imageObject,
    setImageObject,
    isImageUploading,
    setIsImageUploading,
  };
};

export default usePickImage;
